import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MessageCircle, Plus, Loader2, Copy, CheckCircle, XCircle,
  Trash2, ChevronDown, ChevronUp, Bot, TrendingUp, MessageSquare, Bell, Settings,
  Users, Zap, AlertTriangle, RefreshCw, UserCog, ShieldCheck, ShieldOff, Search, X
} from 'lucide-react';
import { API_URL } from '../../config';
import { SalesPanel, ConversationsPanel, TelegramPanel } from '../client/PagePanels';
import BotConfigPanel from '../shared/BotConfigPanel';

// ============================================================
// ADMIN - Connexions WhatsApp (via SendPulse)
// Même cerveau que Messenger : Bot Studio, ventes, conversations,
// alertes Telegram — tout est réutilisé, seule la connexion diffère
// (pas d'OAuth : numéro vérifié manuellement dans SendPulse).
// + Quota de contacts SendPulse (partagé entre tous les bots) et
//   nettoyage des contacts inactifs.
// ============================================================

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json'
});

const PLAN_LIMIT = 500; // à ajuster si le forfait SendPulse change

export default function WhatsAppConnections() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', whatsapp_number: '', owner_user_id: '', channel: 'whatsapp' });
  const [message, setMessage] = useState(null);
  const [expanded, setExpanded] = useState(null); // client_id ouvert
  const [panelTab, setPanelTab] = useState('config');
  const [copiedSlug, setCopiedSlug] = useState(null);
  const [clientUsers, setClientUsers] = useState([]); // comptes clients TeckiA (pour lier une connexion)
  const [contactsModalBot, setContactsModalBot] = useState(null); // bot ouvert dans la modal Contacts

  // Quota SendPulse
  const [quota, setQuota] = useState(null);      // { total_contacts, total_bots, active_bots, bots: [...] }
  const [quotaLoading, setQuotaLoading] = useState(true);
  const botIdByClient = {};
  const platformByClient = {};
  (quota?.bots || []).forEach(b => {
    if (b.client_id) { botIdByClient[b.client_id] = b.bot_id; platformByClient[b.client_id] = b.platform; }
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bot/whatsapp/connections`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setConnections(data.connections);
    } finally { setLoading(false); }
  }, []);

  const loadQuota = useCallback(async () => {
    setQuotaLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bot/whatsapp/quota-overview`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setQuota(data);
    } finally { setQuotaLoading(false); }
  }, []);

  const loadClientUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/bot/page-limits`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setClientUsers(data.users);
    } catch (e) { /* liste de confort, pas bloquant */ }
  }, []);

  useEffect(() => { load(); loadQuota(); loadClientUsers(); }, [load, loadQuota, loadClientUsers]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || (form.channel === 'whatsapp' && !form.whatsapp_number.trim())) return;
    setCreating(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/bot/whatsapp/connections`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(form)
      });
      const data = await res.json();
      setMessage({ type: data.success ? 'ok' : 'error', text: data.message });
      if (data.success) {
        setForm({ name: '', whatsapp_number: '', owner_user_id: '', channel: 'whatsapp' });
        setShowForm(false);
        load();
      }
    } finally { setCreating(false); }
  };

  const linkOwner = async (clientId, userId) => {
    const res = await fetch(`${API_URL}/api/bot/whatsapp/connections/${clientId}/link-owner`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ user_id: userId })
    });
    const data = await res.json();
    if (data.success) {
      setMessage({ type: 'ok', text: `Connexion liee a ${data.owner_name || data.owner_email}` });
      load();
    } else {
      setMessage({ type: 'error', text: data.message });
    }
  };

  const toggleSelfConfig = async (clientId) => {
    const res = await fetch(`${API_URL}/api/bot/config/${clientId}/allow-self-config`, {
      method: 'POST', headers: authHeaders()
    });
    const data = await res.json();
    if (data.success) load();
  };

  const linkBot = async (clientId, botId) => {
    const res = await fetch(`${API_URL}/api/bot/whatsapp/connections/${clientId}/link-bot`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ bot_id: botId })
    });
    const data = await res.json();
    setMessage({ type: data.success ? 'ok' : 'error', text: data.message });
    if (data.success) loadQuota();
  };

  const remove = async (conn) => {
    if (!window.confirm(`Désactiver la connexion WhatsApp « ${conn.name} » ?`)) return;
    await fetch(`${API_URL}/api/bot/whatsapp/connections/${conn.client_id}`, {
      method: 'DELETE', headers: authHeaders()
    });
    load();
  };

  const copyUrl = (conn) => {
    navigator.clipboard.writeText(conn.webhook_url);
    setCopiedSlug(conn.client_id);
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  const toggleExpand = (clientId) => {
    if (expanded === clientId) { setExpanded(null); return; }
    setExpanded(clientId);
    setPanelTab('config');
  };

  const pct = quota ? Math.min(100, Math.round((quota.total_contacts / PLAN_LIMIT) * 100)) : 0;
  const pctColor = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-1 flex items-center gap-3">
            <MessageCircle className="w-8 h-8 text-green-400" />
            Messagerie via SendPulse
          </h1>
          <p className="text-sm text-blue-200">
            WhatsApp + Facebook Messenger (temporaire, en attendant l'App Review) — même agent IA,
            Bot Studio, ventes, conversations, alertes Telegram. La Page API officielle Facebook est un
            module séparé, non affecté par cette page.
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-green-600 hover:bg-green-500 text-white font-medium transition-all"
        >
          <Plus className="w-4 h-4" /> Nouvelle connexion
        </button>
      </div>

      {/* ── QUOTA SENDPULSE (partagé entre tous les bots) ── */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-5 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-green-300" />
            <h2 className="text-white font-bold text-sm md:text-base">Quota de contacts SendPulse</h2>
          </div>
          <button onClick={loadQuota} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white">
            <RefreshCw className={`w-4 h-4 ${quotaLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {quotaLoading && !quota ? (
          <div className="py-6 text-center"><Loader2 className="w-6 h-6 text-green-400 animate-spin mx-auto" /></div>
        ) : quota ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                <div className="text-gray-400 text-xs mb-1">Contacts utilisés</div>
                <div className="text-white font-bold text-2xl">{quota.total_contacts} <span className="text-gray-500 text-sm font-normal">/ {PLAN_LIMIT}</span></div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                <div className="text-gray-400 text-xs mb-1 flex items-center gap-1"><Zap className="w-3.5 h-3.5" /> Bots actifs</div>
                <div className="text-white font-bold text-2xl">{quota.active_bots} <span className="text-gray-500 text-sm font-normal">/ {quota.total_bots}</span></div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                <div className="text-gray-400 text-xs mb-1">Marge restante</div>
                <div className="text-white font-bold text-2xl">{Math.max(0, PLAN_LIMIT - quota.total_contacts)}</div>
              </div>
            </div>

            <div className="w-full h-2.5 bg-white/10 rounded-full overflow-hidden mb-1">
              <div className={`h-full ${pctColor} transition-all`} style={{ width: `${pct}%` }} />
            </div>
            {pct >= 80 && (
              <div className="flex items-center gap-2 text-yellow-300 text-xs mt-2">
                <AlertTriangle className="w-3.5 h-3.5" /> Approche du plafond — pensez à nettoyer les contacts inactifs ou upgrader le forfait SendPulse.
              </div>
            )}

            {quota.bots.length > 0 && (
              <div className="mt-4 space-y-2">
                {quota.bots.map(b => (
                  <button
                    key={b.bot_id}
                    type="button"
                    onClick={() => setContactsModalBot(b)}
                    className="w-full flex items-center justify-between gap-3 bg-black/20 hover:bg-black/30 rounded-xl px-4 py-2.5 text-sm text-left transition-all"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${b.status === 'active' ? 'bg-green-400' : 'bg-gray-500'}`} />
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs flex-shrink-0 ${
                        b.platform === 'facebook' ? 'bg-blue-500/20 text-blue-300' : 'bg-green-500/20 text-green-300'
                      }`}>
                        {b.platform === 'facebook' ? <MessageSquare className="w-3 h-3" /> : <MessageCircle className="w-3 h-3" />}
                        {b.platform === 'facebook' ? 'Messenger' : 'WhatsApp'}
                      </span>
                      <span className="text-white font-medium truncate">{b.name}</span>
                      {b.phone && <span className="text-gray-500 text-xs">+{b.phone}</span>}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-400 flex-shrink-0">
                      <span>{b.contacts_total} contact{b.contacts_total !== 1 ? 's' : ''}</span>
                      <span>{b.outgoing_messages} envoyés</span>
                      <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="text-gray-500 text-sm">Impossible de charger le quota SendPulse.</p>
        )}
      </div>

      {message && (
        <div className={`p-4 rounded-2xl flex items-center gap-3 text-sm ${
          message.type === 'ok'
            ? 'bg-green-500/10 border border-green-500/30 text-green-300'
            : 'bg-red-500/10 border border-red-500/30 text-red-300'
        }`}>
          {message.type === 'ok' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {showForm && (
        <form onSubmit={create} className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3">
          <div className="flex gap-2">
            {[
              { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: 'green' },
              { id: 'facebook', label: 'Facebook Messenger', icon: MessageSquare, color: 'blue' },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setForm(prev => ({ ...prev, channel: id }))}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  form.channel === id ? 'bg-green-600 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'
                }`}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Nom de l'entreprise / page"
              required
              className="bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500"
            />
            {form.channel === 'whatsapp' && (
              <input
                value={form.whatsapp_number}
                onChange={(e) => setForm(prev => ({ ...prev, whatsapp_number: e.target.value }))}
                placeholder="Numéro WhatsApp (+261...)"
                required
                className="bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500"
              />
            )}
          </div>
          {form.channel === 'facebook' && (
            <p className="text-blue-200 text-xs bg-blue-500/10 border border-blue-500/20 rounded-xl p-3">
              Pour Facebook, connectez d'abord la Page dans SendPulse (Chatbots → Facebook Messenger →
              Connect avec le compte Facebook ayant les droits sur la Page), puis créez la connexion ici avec
              le même nom pour que le rapprochement automatique fonctionne.
            </p>
          )}
          <ClientPicker
            value={form.owner_user_id}
            onChange={(v) => setForm(prev => ({ ...prev, owner_user_id: v }))}
            clientUsers={clientUsers}
            placeholder="Compte client (optionnel — sinon assigné à vous)"
          />
          <button type="submit" disabled={creating}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-green-600 hover:bg-green-500 text-white font-medium disabled:opacity-50">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Créer la connexion
          </button>
        </form>
      )}

      {/* Instructions SendPulse */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-4 text-blue-200 text-xs leading-relaxed">
        <strong className="text-blue-100">Configuration SendPulse (par connexion) :</strong> Chatbots → WhatsApp →
        connectez le numéro (via « Continue with Facebook », vérification Meta gérée par SendPulse) → désactivez le
        déclencheur <strong>« Standard reply »</strong> par défaut (sinon il répond à la place de notre agent) →
        allez dans <strong>Bot Settings → Webhooks</strong>, activez, collez l'URL ci-dessous, cochez uniquement
        <strong> « Incoming message »</strong>, puis Save. Notre backend envoie ensuite la réponse directement via
        l'API SendPulse — rien d'autre à configurer.
      </div>

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="w-10 h-10 text-green-400 animate-spin mx-auto" /></div>
      ) : connections.length === 0 ? (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-10 text-center">
          <MessageCircle className="w-12 h-12 text-gray-500 mx-auto mb-3" />
          <p className="text-gray-400">Aucune connexion pour le moment.</p>
        </div>
      ) : (
        <>
          <ConnectionGroup
            title="📱 WhatsApp"
            color="green"
            connections={connections.filter(c => c.channel !== 'facebook')}
            expanded={expanded} panelTab={panelTab} copiedSlug={copiedSlug}
            botIdByClient={botIdByClient} platformByClient={platformByClient}
            clientUsers={clientUsers} allBots={quota?.bots || []}
            onCopy={copyUrl} onExpand={toggleExpand} onSetTab={setPanelTab} onRemove={remove} onDeleted={loadQuota}
            onLinkOwner={linkOwner} onToggleSelfConfig={toggleSelfConfig} onLinkBot={linkBot}
          />
          <ConnectionGroup
            title="💬 Facebook Messenger (via SendPulse — temporaire)"
            color="blue"
            connections={connections.filter(c => c.channel === 'facebook')}
            expanded={expanded} panelTab={panelTab} copiedSlug={copiedSlug}
            botIdByClient={botIdByClient} platformByClient={platformByClient}
            clientUsers={clientUsers} allBots={quota?.bots || []}
            onCopy={copyUrl} onExpand={toggleExpand} onSetTab={setPanelTab} onRemove={remove} onDeleted={loadQuota}
            onLinkOwner={linkOwner} onToggleSelfConfig={toggleSelfConfig} onLinkBot={linkBot}
          />
        </>
      )}

      {contactsModalBot && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setContactsModalBot(null)}>
          <div className="bg-slate-900 border border-white/10 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/10 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-white font-bold text-lg truncate">{contactsModalBot.name}</h3>
                <p className="text-gray-400 text-xs">
                  {contactsModalBot.platform === 'facebook' ? 'Messenger' : 'WhatsApp'} — {contactsModalBot.contacts_total} contact{contactsModalBot.contacts_total !== 1 ? 's' : ''}, {contactsModalBot.outgoing_messages} envoyés
                </p>
              </div>
              <button onClick={() => setContactsModalBot(null)} className="p-2 rounded-lg hover:bg-white/10 text-gray-400 flex-shrink-0">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto">
              <ContactsPanel
                botId={contactsModalBot.bot_id}
                platform={contactsModalBot.platform}
                onDeleted={loadQuota}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// Sélecteur de client avec recherche (nom / email) — remplace un
// <select> classique devenu impraticable avec beaucoup de comptes.
// ════════════════════════════════════════════════════════════
function ClientPicker({ value, onChange, clientUsers, placeholder, small }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const selected = clientUsers.find(u => u.user_id === value);
  const q = search.trim().toLowerCase();
  const filtered = q
    ? clientUsers.filter(u => (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
    : clientUsers;

  return (
    <div ref={wrapRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between gap-2 bg-white/10 border border-white/20 rounded-xl text-white text-left ${
          small ? 'px-3 py-2 text-xs rounded-lg' : 'px-4 py-2.5 text-sm'
        }`}
      >
        <span className={`truncate ${selected ? 'text-white' : 'text-gray-500'}`}>
          {selected ? `${selected.name || selected.email} — ${selected.email}` : (placeholder || 'Choisir un client')}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-slate-900 border border-white/20 rounded-xl shadow-xl max-h-72 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-white/10 flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-gray-500 flex-shrink-0 ml-1" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom ou email..."
              className="w-full bg-transparent text-white text-xs placeholder-gray-500 outline-none py-1.5"
            />
          </div>
          <div className="overflow-y-auto">
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
              className="w-full text-left px-4 py-2 text-xs text-gray-400 hover:bg-white/10"
            >
              {placeholder && placeholder.startsWith('—') ? placeholder : '— Non lié à un client —'}
            </button>
            {filtered.length === 0 ? (
              <div className="px-4 py-3 text-xs text-gray-500">Aucun résultat</div>
            ) : filtered.map(u => (
              <button
                key={u.user_id}
                type="button"
                onClick={() => { onChange(u.user_id); setOpen(false); setSearch(''); }}
                className={`w-full text-left px-4 py-2 text-xs hover:bg-white/10 ${value === u.user_id ? 'bg-green-500/20 text-green-300' : 'text-white'}`}
              >
                {u.name || u.email} — {u.email}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// Groupe de connexions (WhatsApp ou Facebook), avec titre de section
// ════════════════════════════════════════════════════════════
function ConnectionGroup({ title, color, connections, expanded, panelTab, copiedSlug, botIdByClient, platformByClient, clientUsers, allBots, onCopy, onExpand, onSetTab, onRemove, onDeleted, onLinkOwner, onToggleSelfConfig, onLinkBot }) {
  if (connections.length === 0) return null;
  return (
    <div className="space-y-3">
      <h3 className="text-white font-semibold text-sm flex items-center gap-2">
        {title}
        <span className="text-gray-500 font-normal text-xs">({connections.length})</span>
      </h3>
      {connections.map(conn => (
        <ConnectionCard
          key={conn.client_id}
          conn={conn}
          color={color}
          isExpanded={expanded === conn.client_id}
          panelTab={panelTab}
          isCopied={copiedSlug === conn.client_id}
          botId={botIdByClient[conn.client_id]}
          platform={platformByClient[conn.client_id] || conn.channel || 'whatsapp'}
          clientUsers={clientUsers}
          allBots={allBots}
          onCopy={onCopy} onExpand={onExpand} onSetTab={onSetTab} onRemove={onRemove} onDeleted={onDeleted}
          onLinkOwner={onLinkOwner} onToggleSelfConfig={onToggleSelfConfig} onLinkBot={onLinkBot}
        />
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// Carte de connexion individuelle (WhatsApp ou Facebook)
// ════════════════════════════════════════════════════════════
function ConnectionCard({ conn, color, isExpanded, panelTab, isCopied, botId, platform, clientUsers, allBots, onCopy, onExpand, onSetTab, onRemove, onDeleted, onLinkOwner, onToggleSelfConfig, onLinkBot }) {
  const accent = color === 'blue' ? 'blue' : 'green';
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
      <div className="p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
            accent === 'blue' ? 'bg-blue-600/30' : 'bg-green-600/30'
          }`}>
            {accent === 'blue'
              ? <MessageSquare className="w-5 h-5 text-blue-300" />
              : <MessageCircle className="w-5 h-5 text-green-300" />}
          </div>
          <div>
            <div className="text-white font-semibold text-sm">{conn.name}</div>
            <div className="text-gray-400 text-xs">
              {[conn.whatsapp_number, conn.owner_email || 'non lie a un client'].filter(Boolean).join(' · ')}
            </div>
          </div>
          <span className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
            conn.bot_active ? 'bg-green-500/20 text-green-300' : 'bg-gray-500/20 text-gray-400'
          }`}>
            <Bot className="w-3 h-3" /> {conn.bot_active ? 'Actif' : 'Pause'}
          </span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
            conn.self_config_allowed ? 'bg-purple-500/20 text-purple-300' : 'bg-gray-500/20 text-gray-500'
          }`}>
            {conn.self_config_allowed ? <ShieldCheck className="w-3 h-3" /> : <ShieldOff className="w-3 h-3" />}
            {conn.self_config_allowed ? 'Config client autorisee' : 'Config client verrouillee'}
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => onCopy(conn)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-gray-300 text-xs">
            <Copy className="w-3.5 h-3.5" /> {isCopied ? 'Copié !' : 'URL webhook'}
          </button>
          <button onClick={() => onExpand(conn.client_id)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-600/30 hover:bg-indigo-600/50 text-indigo-200 text-xs font-medium">
            <Settings className="w-3.5 h-3.5" /> Gérer
            {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          <button onClick={() => onRemove(conn)}
            className="p-2 rounded-lg bg-red-500/10 text-red-300 hover:bg-red-500/20">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-white/10 p-4 md:p-5 bg-black/20">
          {/* Lier a un compte client + valider l'acces config */}
          <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4 bg-white/5 border border-white/10 rounded-xl p-3">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <UserCog className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <ClientPicker
                value={conn.owner_user_id || ''}
                onChange={(v) => v && onLinkOwner(conn.client_id, v)}
                clientUsers={clientUsers}
                placeholder="— Non lié à un client —"
                small
              />
            </div>
            <button
              onClick={() => onToggleSelfConfig(conn.client_id)}
              disabled={!conn.owner_user_id}
              title={!conn.owner_user_id ? 'Liez d\'abord un compte client' : ''}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all disabled:opacity-40 flex-shrink-0 ${
                conn.self_config_allowed
                  ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30'
                  : 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
              }`}
            >
              {conn.self_config_allowed ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldOff className="w-3.5 h-3.5" />}
              {conn.self_config_allowed ? 'Retirer l\'acces client' : 'Valider l\'acces config client'}
            </button>
          </div>

          <div className="flex gap-2 flex-wrap mb-4">
            {[
              { tab: 'config', icon: Bot, label: 'Configuration de l\'agent' },
              { tab: 'sales', icon: TrendingUp, label: 'Ventes' },
              { tab: 'conversations', icon: MessageSquare, label: 'Conversations' },
              { tab: 'telegram', icon: Bell, label: 'Alertes Telegram' },
              { tab: 'contacts', icon: Users, label: 'Contacts' },
            ].map(({ tab, icon: Icon, label }) => (
              <button
                key={tab}
                onClick={() => onSetTab(tab)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium transition-all ${
                  panelTab === tab ? 'bg-green-600 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>

          {panelTab === 'config' && <BotConfigPanel clientId={conn.client_id} />}
          {panelTab === 'sales' && <SalesPanel clientId={conn.client_id} />}
          {panelTab === 'conversations' && <ConversationsPanel clientId={conn.client_id} />}
          {panelTab === 'telegram' && <TelegramPanel clientId={conn.client_id} />}
          {panelTab === 'contacts' && (
            botId
              ? <ContactsPanel botId={botId} platform={platform} onDeleted={onDeleted} />
              : <BotLinkPicker platform={platform} allBots={allBots} onLink={(botId2) => onLinkBot(conn.client_id, botId2)} />
          )}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// Lien manuel vers un bot SendPulse — quand le matching automatique
// (téléphone pour WhatsApp, nom pour Facebook) ne trouve rien.
// ════════════════════════════════════════════════════════════
function BotLinkPicker({ platform, allBots, onLink }) {
  const [selected, setSelected] = useState('');
  const options = (allBots || []).filter(b => b.platform === platform);

  if (options.length === 0) {
    return (
      <p className="text-gray-400 text-sm py-4">
        Aucun bot {platform === 'facebook' ? 'Messenger' : 'WhatsApp'} trouvé côté SendPulse pour le moment —
        vérifiez que le quota (ci-dessus) a été rafraîchi.
      </p>
    );
  }

  return (
    <div className="py-4 space-y-3">
      <div className="flex items-start gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3">
        <AlertTriangle className="w-4 h-4 text-yellow-300 flex-shrink-0 mt-0.5" />
        <p className="text-yellow-200 text-xs">
          Bot SendPulse pas détecté automatiquement pour cette connexion (le nom ne correspond pas exactement
          — ça arrive surtout sur Facebook). Choisissez manuellement le bon bot ci-dessous.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="flex-1 bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-white text-sm"
        >
          <option value="" className="bg-slate-900">— Choisir le bot SendPulse —</option>
          {options.map(b => (
            <option key={b.bot_id} value={b.bot_id} className="bg-slate-900">
              {b.name}{b.phone ? ` (+${b.phone})` : ''} — {b.contacts_total} contact{b.contacts_total !== 1 ? 's' : ''}
              {b.client_id ? ' · déjà lié ailleurs' : ''}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!selected}
          onClick={() => onLink(selected)}
          className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium disabled:opacity-40 flex-shrink-0"
        >
          Lier
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// 👥 PANNEAU CONTACTS (liste + suppression des inactifs)
// ════════════════════════════════════════════════════════════
function ContactsPanel({ botId, platform = 'whatsapp', onDeleted }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setSelected([]);
    try {
      const res = await fetch(`${API_URL}/api/bot/whatsapp/bot-contacts/${botId}?platform=${platform}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setContacts(data.contacts);
    } finally { setLoading(false); }
  }, [botId, platform]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const selectInactive = (days) => {
    setSelected(contacts.filter(c => (c.days_inactive ?? 0) >= days).map(c => c.contact_id));
  };

  const deleteSelected = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Supprimer définitivement ${selected.length} contact(s) ? Cette action est irréversible côté SendPulse.`)) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_URL}/api/bot/whatsapp/contacts/delete`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ contact_ids: selected, platform })
      });
      const data = await res.json();
      if (data.success) {
        load();
        onDeleted && onDeleted();
      }
    } finally { setDeleting(false); }
  };

  if (loading) return <div className="py-8 text-center"><Loader2 className="w-6 h-6 text-green-400 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-gray-400 text-xs">{contacts.length} contact{contacts.length !== 1 ? 's' : ''} pour ce bot</p>
        <div className="flex gap-2">
          <button onClick={() => selectInactive(30)} className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300 text-xs">
            Sélectionner inactifs 30j+
          </button>
          <button onClick={() => selectInactive(90)} className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300 text-xs">
            90j+
          </button>
          <button onClick={load} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {contacts.length === 0 ? (
        <p className="text-gray-500 text-sm py-4 text-center">Aucun contact pour ce bot.</p>
      ) : (
        <div className="space-y-1.5 max-h-96 overflow-y-auto">
          {contacts.map(c => (
            <label key={c.contact_id} className="flex items-center gap-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-3 py-2.5 cursor-pointer">
              <input type="checkbox" checked={selected.includes(c.contact_id)} onChange={() => toggle(c.contact_id)}
                className="w-4 h-4 accent-green-500" />
              <span className="text-white text-sm flex-1">{c.name}</span>
              <span className={`text-xs ${
                c.days_inactive === null ? 'text-gray-500' : c.days_inactive >= 60 ? 'text-red-300' : c.days_inactive >= 30 ? 'text-yellow-300' : 'text-gray-400'
              }`}>
                {c.days_inactive === null ? 'Jamais actif' : c.days_inactive === 0 ? 'Actif aujourd\'hui' : `Inactif depuis ${c.days_inactive}j`}
              </span>
            </label>
          ))}
        </div>
      )}

      {selected.length > 0 && (
        <button
          onClick={deleteSelected}
          disabled={deleting}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-red-600 hover:bg-red-500 text-white font-medium text-sm disabled:opacity-50"
        >
          {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          Supprimer {selected.length} contact{selected.length !== 1 ? 's' : ''} sélectionné{selected.length !== 1 ? 's' : ''}
        </button>
      )}
    </div>
  );
}
